/**
 * Barre de recherche permanente du header (desktop).
 *
 * Enveloppe un <predictive-search-component> du thème. La recherche et l'affichage des résultats
 * restent entièrement gérés par ce composant ; cet élément fait seulement deux choses :
 *
 * 1. Ouvrir / fermer le menu de résultats (attribut `open`, utilisé par le CSS).
 *    - Ouverture : survol puis clic, focus clavier, saisie.
 *    - Fermeture : clic ou focus en dehors, Échap, scroll de la page, retour arrière du navigateur.
 *
 * 2. Charger le contenu initial du menu (produits vus récemment, collection) au premier survol
 *    ou clic, via `resetSearch()`, la méthode publique du composant. Le contenu n'est pas rendu
 *    côté serveur dans le snippet : le snippet de contenu utilise {% paginate %}, déjà employé
 *    par la modale de recherche.
 */
class HeaderSearchBar extends HTMLElement {
  #controller = new AbortController();

  #contentRequested = false;

  connectedCallback() {
    const { signal } = this.#controller;

    this.addEventListener('pointerenter', this.#ensureContent, { signal });
    this.addEventListener('pointerdown', this.#open, { signal });
    this.addEventListener('focusin', this.#open, { signal });
    this.addEventListener('input', this.#open, { signal });
    this.addEventListener('click', this.#onClick, { signal });

    document.addEventListener('pointerdown', this.#closeIfOutside, { signal, capture: true });
    document.addEventListener('focusin', this.#closeIfOutside, { signal });
    document.addEventListener('keydown', this.#onKeyDown, { signal });
    window.addEventListener('scroll', this.#close, { signal, passive: true });
    window.addEventListener('pageshow', this.#close, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#controller = new AbortController();
  }

  get #input() {
    return this.querySelector('input[type="search"]');
  }

  #open = () => {
    this.#ensureContent();
    this.setAttribute('open', '');
    this.#input?.setAttribute('aria-expanded', 'true');
  };

  #close = () => {
    this.removeAttribute('open');
    this.#input?.setAttribute('aria-expanded', 'false');
  };

  /**
   * Charge le contenu initial du menu une seule fois, s'il n'est pas déjà présent.
   */
  #ensureContent = () => {
    if (this.#contentRequested || this.#input?.value) return;

    const results = this.querySelector('.predictive-search-form__content');
    if (!results || results.childElementCount > 0) {
      this.#contentRequested = true;
      return;
    }

    /** @type {any} */
    const search = this.querySelector('predictive-search-component');

    // Le script du composant n'est pas encore chargé : on réessaiera au prochain événement.
    if (typeof search?.resetSearch !== 'function') return;

    this.#contentRequested = true;
    search.resetSearch(false);
  };

  /**
   * Clic sur la loupe : lance la recherche si un terme est saisi, sinon place le curseur dans le champ.
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('[data-search-submit]')) return;

    const input = this.#input;
    if (!(input instanceof HTMLInputElement)) return;

    if (input.value.trim()) {
      input.form?.requestSubmit();
    } else {
      input.focus();
    }
  };

  /**
   * @param {Event} event
   */
  #closeIfOutside = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) {
      this.#close();
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#close();
    }
  };
}

if (!customElements.get('header-search-bar')) {
  customElements.define('header-search-bar', HeaderSearchBar);
}
